import { useState, useEffect, useCallback, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  useCreateSite,
  useUpdateSite,
  useCreateSiteBlueprint,
  useSites,
  useOrganizations,
  siteKeys,
} from '../../../hooks/queries';
import {
  getSiteById,
  type Site as ApiSite,
  type SiteStatus,
} from '../../../services';
import {
  SiteFormInitialValues,
  SiteFormInitialValuesObj,
} from '../../../types/SitesFormInitialValue';
import type { Building, Floor, Room } from '../../../services';
import type { StatusType } from '@themes/aegix';
import { useSearchParams } from 'react-router-dom';
import { type UseFormSetError } from 'react-hook-form';
import type { StagedBlueprint } from '../drawers/BlueprintsStaging';
import { toaster } from '../../ui/toaster';
import { ApiValidationError } from '../../../lib/http-client';
import { setSchoolPreselectedProviderOrgs } from '../../../services/perSite.service';

export interface SiteRow {
  id: string;
  name: string;
  address: string;
  city: string;
  status: StatusType;
  latitude?: string;
  longitude?: string;
  elevation?: string;
  // Raw fields for SiteDrawer editing
  raw_address?: string;
  address_2?: string;
  raw_city?: string;
  state?: string;
  zip?: string;
  country?: string;
  phone_number?: string;
  // Numeric District FK — the value the org selector binds to and the
  // payload sends as `district`. Carried alongside the display-only
  // organization_public_id/name so a future edit drawer can pre-select the org.
  district?: number | null;
  organization_public_id?: string;
  organization_name?: string;
  drill_enabled?: boolean;
  show_safe_unsafe?: boolean;
  licensed?: boolean;
}

interface Option {
  label: string;
  value: string;
}

const statusMap: Record<SiteStatus, StatusType> = {
  active: 'active',
  pending: 'pending',
  scoped: 'pending',
  inactive: 'inactive',
};

function transformSites(apiSites: ApiSite[]): SiteRow[] {
  return apiSites.map((site) => ({
    id: String(site.id),
    name: site.name,
    address: [site.address, site.address_2].filter(Boolean).join(', ') || '-',
    city: [site.city, site.state].filter(Boolean).join(', ') || '-',
    status: statusMap[site.status],
    latitude: site.latitude,
    longitude: site.longitude,
    elevation: site.elevation,
    raw_address: site.address,
    address_2: site.address_2,
    raw_city: site.city,
    state: site.state,
    zip: site.zip,
    country: site.country,
    phone_number: site.phone_number,
    district: site.district,
    organization_public_id: site.organization_public_id,
    organization_name: site.organization_name,
    drill_enabled: site.drill_enabled,
    show_safe_unsafe: site.show_safe_unsafe,
    licensed: site.licensed,
  }));
}

export function useSitesFloorPlansData() {
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const urlId = searchParams.get('id');
  const urlTab = searchParams.get('tab');
  const isFloorplansTab = !urlTab || urlTab === 'floorplans';
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchValue, setSearchValue] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(8);
  const [sortColumn, setSortColumn] = useState<string | undefined>();
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc' | null>(
    null,
  );
  const [filterValues, setFilterValues] = useState<Record<string, string>>({});

  // Site edit drawer state (purely local UI — keeps useState)
  const [editOpen, setEditOpen] = useState(false);
  const [action, setAction] = useState('');
  const [currentRow, setCurrentRow] = useState<SiteFormInitialValues>(
    SiteFormInitialValuesObj,
  );
  const createSite = useCreateSite();
  const updateSite = useUpdateSite();
  const createBlueprint = useCreateSiteBlueprint();

  // Staged blueprints — collected in the add drawer, uploaded sequentially
  // after createSite resolves (the API needs a siteId).
  const [staged, setStaged] = useState<StagedBlueprint[]>([]);

  // Cascading drawer state
  const [selectedSite, setSelectedSite] = useState<SiteRow | null>(null);
  const [selectedBuilding, setSelectedBuilding] = useState<Building | null>(
    null,
  );
  const [selectedFloor, setSelectedFloor] = useState<Floor | null>(null);
  const [selectedRoom, setSelectedRoom] = useState<Room | null>(null);

  const formDefaultValues = useMemo(
    () =>
      action === 'edit' && currentRow
        ? { ...SiteFormInitialValuesObj, ...currentRow }
        : { ...SiteFormInitialValuesObj },
    [action, currentRow],
  );

  // Sites list goes through `useSites` (React Query, ADR 102) rather than a
  // manual `useState + useEffect + try/catch` fetcher, so
  // `AuthContext.setActiveOrganization`'s blanket invalidation refreshes this
  // hook on org switch.
  const sitesFilters = useMemo(
    () => ({
      // LimitOffsetPagination takes a row offset, not a page index.
      offset: (currentPage - 1) * pageSize,
      limit: pageSize,
      query: searchValue || undefined,
      ordering: sortColumn
        ? `${sortDirection === 'desc' ? '-' : ''}${sortColumn}`
        : undefined,
      status: statusFilter !== 'all' ? (statusFilter as SiteStatus) : undefined,
    }),
    [
      currentPage,
      pageSize,
      searchValue,
      sortColumn,
      sortDirection,
      statusFilter,
    ],
  );

  const useSingleSitePath = !!(urlId && isFloorplansTab);

  const {
    data: sitesResponse,
    isLoading: sitesListLoading,
    error: sitesListError,
  } = useSites(sitesFilters);

  // Single-site path (URL id present + floorplans tab) — fetch the named
  // site by id instead of the paginated list.
  const { data: siteData, isLoading: siteDataLoading } = useQuery({
    queryKey: ['getSiteById', urlId],
    queryFn: async () => {
      if (!urlId || !isFloorplansTab) return null;
      const response = await getSiteById(Number(urlId));
      return response;
    },
    enabled: useSingleSitePath,
  });

  const sites = useMemo<SiteRow[]>(() => {
    if (useSingleSitePath) {
      return siteData ? transformSites([siteData as ApiSite]) : [];
    }
    return transformSites(sitesResponse?.results ?? []);
  }, [useSingleSitePath, siteData, sitesResponse]);

  const totalItems = useSingleSitePath
    ? siteData
      ? 1
      : 0
    : (sitesResponse?.count ?? 0);

  const isLoading = useSingleSitePath ? siteDataLoading : sitesListLoading;
  const error = useSingleSitePath ? null : sitesListError;

  // Organization options for the site form — React Query–backed so it
  // re-fetches on org switch alongside everything else.
  const { data: orgsData } = useOrganizations();
  const organizationOptions = useMemo<Option[]>(
    () => (orgsData ?? []).map((o) => ({ label: o.label, value: o.value })),
    [orgsData],
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [searchValue, statusFilter, pageSize]);

  const handleFilterChange = (column: string, value: string) => {
    setFilterValues((prev) => ({ ...prev, [column]: value }));
  };

  const handleFilterClear = (column: string) => {
    setFilterValues((prev) => {
      const next = { ...prev };
      delete next[column];
      return next;
    });
  };

  const filteredSites = useMemo(() => {
    const activeFilters = Object.entries(filterValues).filter(([, v]) => v);
    if (activeFilters.length === 0) return sites;
    return sites.filter((row) =>
      activeFilters.every(([col, val]) => {
        const cellValue = String(
          (row as unknown as Record<string, unknown>)[col] ?? '',
        ).toLowerCase();
        return cellValue.includes(val.toLowerCase());
      }),
    );
  }, [sites, filterValues]);

  const handleSort = useCallback(
    (column: string) => {
      if (sortColumn === column) {
        setSortDirection((prev) =>
          prev === 'asc' ? 'desc' : prev === 'desc' ? null : 'asc',
        );
        if (sortDirection === 'desc') setSortColumn(undefined);
      } else {
        setSortColumn(column);
        setSortDirection('asc');
      }
    },
    [sortColumn, sortDirection],
  );

  // useUpdateSite's onSuccess already invalidates siteKeys.lists();
  // calling explicitly here is a no-op safety net for any future change that drops that.
  const invalidateSitesList = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: siteKeys.lists() });
  }, [queryClient]);

  const handleSubmit = async (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    values: any,
    setError: UseFormSetError<SiteFormInitialValues>,
  ) => {
    // Surface server-side validation (e.g. "Site with this name already
    // exists.") inline on the offending field plus a toast — the same
    // ApiValidationError handling every other SMS CRUD drawer uses. Without
    // this the create/update 400 was logged to console only, so the drawer
    // sat open with no feedback and users re-clicked Save repeatedly.
    const onError = (err: Error) => {
      if (err instanceof ApiValidationError) {
        for (const [field, messages] of Object.entries(err.fieldErrors)) {
          setError(field as keyof SiteFormInitialValues, {
            message: messages.join(', '),
          });
        }
      }
      toaster.create({
        title: 'Save failed',
        description: err.message,
        type: 'error',
      });
    };

    // The form's `organization` field holds the numeric District id
    // (as a string). The backend's writable FK is `district` — SchoolSerializer
    // exposes only read-only `organization_*` fields — so translate before
    // sending. Without this the selected org is silently dropped and the site
    // is created with no organization (district = NULL).
    const { organization, preselected_provider_ids, ...rest } = values;
    const payload = {
      ...rest,
      district: organization ? Number(organization) : null,
    };

    if (action === 'edit') {
      updateSite.mutate(
        { id: Number(currentRow?.id), data: payload },
        {
          onSuccess: () => {
            handleDrawerClose();
            invalidateSitesList();
          },
          onError,
        },
      );
      return;
    }

    let newSite: ApiSite | undefined;
    try {
      newSite = await createSite.mutateAsync(payload);
    } catch (err) {
      onError(err as Error);
      return;
    }

    if (
      newSite?.id &&
      preselected_provider_ids &&
      preselected_provider_ids.length > 0
    ) {
      try {
        await setSchoolPreselectedProviderOrgs({
          school_id: newSite.id,
          provider_org_ids: preselected_provider_ids,
        });
      } catch (err: unknown) {
        console.error(
          'Failed to set preselected provider orgs for new site:',
          err,
        );
        const errObj = err as {
          detail?: string;
          message?: string;
        };
        const rawDetail = errObj?.detail || errObj?.message;
        const detail =
          typeof rawDetail === 'string' && rawDetail.trim()
            ? rawDetail.replace(/^provider_org_ids:\s*/i, '')
            : 'Failed to set pre-selected provider organizations';
        toaster.create({
          title: 'Site created, but setting provider organizations failed',
          description: detail,
          type: 'error',
          duration: 6000,
        });
      }
    }

    // Snapshot the queue so iteration isn't perturbed by setStaged updates.

    const queue = staged;

    if (!newSite?.id || queue.length === 0) {
      handleDrawerClose();
      invalidateSitesList();
      return;
    }

    const siteId = newSite.id;
    let failures = 0;

    for (const item of queue) {
      setStaged((prev) =>
        prev.map((s) =>
          s.localId === item.localId
            ? { ...s, status: 'uploading' as const, errorMessage: undefined }
            : s,
        ),
      );
      try {
        await createBlueprint.mutateAsync({
          siteId,
          file: item.file,
          label: item.label,
        });
        setStaged((prev) => prev.filter((s) => s.localId !== item.localId));
      } catch (err) {
        failures += 1;
        const message = err instanceof Error ? err.message : 'Upload failed';
        setStaged((prev) =>
          prev.map((s) =>
            s.localId === item.localId
              ? { ...s, status: 'failed' as const, errorMessage: message }
              : s,
          ),
        );
      }
    }

    invalidateSitesList();

    if (failures === 0) {
      handleDrawerClose();
    } else {
      toaster.create({
        title:
          failures === queue.length
            ? 'All blueprint uploads failed'
            : 'Some blueprint uploads failed',
        description:
          failures === queue.length
            ? 'The site was created but no blueprints uploaded. Retry from this drawer.'
            : `The site was created. ${failures} of ${queue.length} blueprints failed.`,
        type: 'error',
        duration: 5000,
      });
    }
  };

  const handleDrawerClose = () => {
    setEditOpen(false);
    setAction('');
    setCurrentRow(SiteFormInitialValuesObj);
    setStaged([]);
  };

  const handleRowClick = (row: SiteRow) => {
    setSelectedSite(row);
    setSelectedBuilding(null);
    setSelectedFloor(null);
    setSelectedRoom(null);
  };

  const closeBuildingDrawer = () => {
    setSelectedSite(null);
    setSelectedBuilding(null);
    setSelectedFloor(null);
    setSelectedRoom(null);
  };

  const totalPages = Math.ceil(totalItems / pageSize);

  const openAddDrawer = () => {
    setEditOpen(true);
    setAction('add');
  };

  return {
    // State
    statusFilter,
    setStatusFilter,
    searchValue,
    setSearchValue,
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    sortColumn,
    sortDirection,
    filterValues,
    editOpen,
    action,
    currentRow,
    staged,
    setStaged,
    selectedSite,
    selectedBuilding,
    setSelectedBuilding,
    selectedFloor,
    setSelectedFloor,
    selectedRoom,
    setSelectedRoom,
    isSaving: createSite.isPending || updateSite.isPending,

    // Data
    sites,
    filteredSites,
    totalItems,
    totalPages,
    isLoading,
    error,
    organizationOptions,
    formDefaultValues,

    // Handlers
    handleFilterChange,
    handleFilterClear,
    handleSort,
    handleSubmit,
    handleDrawerClose,
    handleRowClick,
    closeBuildingDrawer,
    openAddDrawer,
  };
}
